import { getRoleUiProfile } from "./profiles.js";
import { hasPermission } from "../services/permission-matcher.js";
import { DEFAULT_WIDGET_ORDER } from "./widgets.js";
/**
 * The terminal fallback, and the reason every other branch may refuse.
 *
 * The `/dashboard` route carries no `ProtectedRoute` permission, so it is
 * open to every authenticated user — including the budget roles, which
 * hold no `dashboard:view`. That is what makes falling back always safe.
 */
export const DASHBOARD_ROUTE = "/dashboard";
/**
 * Where each intent goes. Permissions are the ones the router declares on
 * the route itself; a test in packages/web pins both halves against the
 * live router and navigation registry, so a route change cannot silently
 * turn a landing into a 403.
 */
const POS = {
    route: "/pos",
    permission: "pos:access",
    modules: ["pos"],
};
const INVOICE_NEW = {
    route: "/invoices/new",
    permission: "invoices:create",
    modules: ["invoices", "ventes-caisse"],
};
const LANDING_TARGETS = {
    // The only sector-dependent intent: a counter trade lands at the till,
    // everyone else at the invoice form. The till is a capability pack, so
    // a profile can set `defaultPosMode` and still not grant `pos`.
    sell: [POS, INVOICE_NEW],
    book: [{ route: "/accounting", permission: "accounting:read", modules: ["accounting-dashboard", "accounting"] }],
    stock: [{ route: "/stock", permission: "stock:read", modules: ["stock"] }],
    // A counter holds no `stock:read`: the inventory list is its station.
    count: [{ route: "/stock/inventaires", permission: "stock:inventory:count", modules: ["inventories"] }],
    verify: [
        {
            route: "/invoices/verification",
            permission: "invoices:verify",
            modules: ["verification", "ventes-caisse"],
        },
    ],
    // `/hr` does not exist; the module's entry point is the employee list.
    hr: [{ route: "/rh/employes", permission: "hr:read", modules: ["hr-employees", "hr"] }],
    // The accounting firm's staff come for their client files.
    cabinet: [
        {
            route: "/cabinet/dossiers",
            permission: "cabinet:access",
            modules: ["cabinet-dossiers", "cabinet"],
        },
    ],
    steer: [{ route: DASHBOARD_ROUTE, permission: null, modules: [] }],
};
/**
 * The candidates for an intent, in the order they are tried.
 *
 * Only `sell` branches on the sector: without POS mode the till is not
 * even a candidate, which keeps a non-counter trade off `/pos` even when
 * its profile happens to carry the pack.
 */
function candidatesFor(intent, profile) {
    if (intent === "sell" && profile.defaultPosMode !== true)
        return [INVOICE_NEW];
    return LANDING_TARGETS[intent];
}
/** Sector term — does the profile put this target in the menu at all? */
function inProfile(target, profile) {
    if (target.modules.length === 0)
        return true;
    if (profile.unrestricted)
        return true;
    return target.modules.some((key) => profile.modules.includes(key));
}
/**
 * The roles this registry knows, highest priority first, `code` breaking
 * ties — the one ordering every role fold uses.
 *
 * `UserRole` is a join table and returns its rows in no guaranteed order,
 * so normalising here is what makes every result below independent of it:
 * a scalar picks the first element, and a union folds in this order.
 */
export function sortedRoleProfiles(roleCodes) {
    const known = [];
    for (const code of roleCodes) {
        const profile = getRoleUiProfile(code);
        if (profile !== null && !known.includes(profile))
            known.push(profile);
    }
    known.sort((a, b) => b.uiPriority !== a.uiPriority
        ? b.uiPriority - a.uiPriority
        : a.code < b.code
            ? -1
            : a.code > b.code
                ? 1
                : 0);
    return known;
}
/**
 * The built-in role whose permissions are closest to `permissions`, for a
 * custom role the UI registry does not know.
 *
 * Both sides are expanded against `catalogue` (every declared permission)
 * with the RBAC matcher, so a wildcard counts as what it grants; closeness
 * is the Jaccard index of the two expansions, which keeps ADMIN's `*` from
 * winning every comparison. Candidates without a role UI profile are
 * skipped; ties go to the `sortedRoleProfiles` order. Null when nothing
 * overlaps, which leaves the role a no-op (I-RP2).
 *
 * @param permissions - the custom role's permissions
 * @param builtIns - built-in role code → its permissions
 * @param catalogue - every permission code the RBAC declares
 */
export function nearestBuiltInRole(permissions, builtIns, catalogue) {
    const expand = (held) => new Set(catalogue.filter((code) => hasPermission(held, code)));
    const own = expand(permissions);
    if (own.size === 0)
        return null;
    let best = null;
    let bestScore = 0;
    for (const role of sortedRoleProfiles(Object.keys(builtIns))) {
        const theirs = expand(builtIns[role.code] ?? []);
        let shared = 0;
        for (const code of own)
            if (theirs.has(code))
                shared += 1;
        const union = own.size + theirs.size - shared;
        const score = union === 0 ? 0 : shared / union;
        if (score > bestScore) {
            best = role.code;
            bestScore = score;
        }
    }
    return best;
}
/** The winning role, or null when the user holds none this registry knows. */
export function pickLandingRole(roleCodes) {
    return sortedRoleProfiles(roleCodes)[0] ?? null;
}
/**
 * The role axis of the UI for every role a user holds.
 *
 * `groupOrder` is a sequence, which cannot union: it comes from the
 * single highest-priority role, even when that role declares none. The
 * two set-shaped fields UNION instead, so a comptable who also runs the
 * store keeps both jobs' widgets rather than losing one of them.
 *
 * I-RP1 — every key returned is a SELECTION. Widgets stay gated on their
 * own permission at render, and `groupOrder` reorders a menu that four
 * other terms have already filtered.
 *
 * I-RP2 — no roles, unknown codes, or roles that declare nothing all
 * return the whole dashboard in its original order and no emphasis at
 * all, which is the screen as it stood before roles composed it.
 */
export function resolveRoleUi(roleCodes) {
    const roles = sortedRoleProfiles(roleCodes);
    const widgets = [];
    const defaultOpenGroups = [];
    for (const role of roles) {
        for (const key of role.widgets ?? []) {
            if (!widgets.includes(key))
                widgets.push(key);
        }
        for (const key of role.defaultOpenGroups ?? []) {
            if (!defaultOpenGroups.includes(key))
                defaultOpenGroups.push(key);
        }
    }
    return {
        widgets: widgets.length > 0 ? widgets : DEFAULT_WIDGET_ORDER,
        groupOrder: roles[0]?.groupOrder ?? [],
        defaultOpenGroups,
    };
}
/**
 * The menu reordered: listed groups hoisted in the order given, the rest
 * following in registry order.
 *
 * A PERMUTATION by construction — every element of `groups` lands in
 * exactly one of the two halves, so no key in `groupOrder` can add a
 * group and no key missing from it can drop one. That is what keeps
 * emphasis from becoming a second access-control axis (I-RP1).
 */
export function applyGroupOrder(groups, groupOrder) {
    if (groupOrder.length === 0)
        return [...groups];
    const hoisted = [];
    for (const key of groupOrder) {
        const found = groups.find((group) => group.key === key);
        if (found !== undefined && !hoisted.includes(found))
            hoisted.push(found);
    }
    return [...hoisted, ...groups.filter((group) => !hoisted.includes(group))];
}
/**
 * Where this user lands after login.
 *
 * I-RP1 — role UI never widens access. `can` is supplied by the caller so
 * that the check runs through the SAME matcher the route gate uses; a
 * target the user cannot open is skipped, never granted.
 *
 * I-RP2 — no roles, unknown codes, or no resolved profile all land on
 * `/dashboard`, which is where every user landed before roles had a say.
 *
 * @param roleCodes - the role codes the user holds, in any order
 * @param profile - the resolved sector profile, or null while unresolved
 * @param can - does the user hold this permission?
 */
export function resolveLandingRoute(roleCodes, profile, can) {
    if (profile === null)
        return DASHBOARD_ROUTE;
    const role = pickLandingRole(roleCodes);
    if (role === null)
        return DASHBOARD_ROUTE;
    for (const target of candidatesFor(role.landingIntent, profile)) {
        if (target.permission !== null && !can(target.permission))
            continue;
        if (!inProfile(target, profile))
            continue;
        return target.route;
    }
    return DASHBOARD_ROUTE;
}
/** The declared targets, for the tests that pin them against the router. */
export function landingTargetsFor(intent) {
    return LANDING_TARGETS[intent];
}
//# sourceMappingURL=resolve.js.map